import React, { useCallback, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { intlShape } from 'react-intl';

import {
  find, get, groupBy, map, sortBy
} from 'lodash';
import classnames from 'classnames';

import { getProductTypeName } from 'helpers/getVarietyName';
import { getPlantingCycleDates } from 'helpers/harvestForecast';
import numbersFormatting from 'helpers/numbersFormatting';
import numbersRounding from 'helpers/numbersRounding';
import { getPlantingCycleProducts } from 'helpers/forecastCycleSelector';

import Select from 'components/Select';

import selectStyles from 'components/Select/Select.module.css';
import styles from './HarvestForecastCycleSelect.module.css';

const formatTargetWeight = targetWeight => (targetWeight
  ? numbersFormatting(numbersRounding(targetWeight, 'fixed', 0))
  : null);

const getCompartmentName = (compartments, compartmentId) => get(
  find(compartments, { id: Number(compartmentId) }),
  'attributes.name'
);

const getGroupName = (payload, varieties, fruitClasses, intl) => {
  const variety = find(varieties, { id: payload?.varietyRef?.id });
  const fruitClass = find(fruitClasses, { id: payload?.fruitClassRef?.id });

  return getProductTypeName({
    intl,
    variety,
    fruitClass,
    targetWeight: formatTargetWeight(payload?.targetWeight),
    withSpecies: false,
    withFruitClass: true,
  });
};

/**
 * Селект цикла для full-page Data assistant. Повторяет вид PlantingCyclesSelect
 * (заголовок + группы по сорту + строки «даты / теплица»), но работает на данных
 * прогноза (manualForecast) и без опции «All», т.к. ассистент всегда показывает один цикл.
 */
const HarvestForecastCycleSelect = ({
  intl,
  productsList,
  varieties,
  fruitClasses,
  compartments,
  selectedId,
  onSelect,
}) => {
  const { formatMessage } = intl;
  const [open, setOpen] = useState(false);

  const cycleProducts = useMemo(() => getPlantingCycleProducts(productsList), [productsList]);

  const groupedByVariety = useMemo(
    () => groupBy(cycleProducts, item => get(item, 'product.payload.varietyRef.id')),
    [cycleProducts]
  );

  const selectedProduct = useMemo(
    () => find(cycleProducts, item => Number(get(item, 'product.id')) === Number(selectedId)),
    [cycleProducts, selectedId]
  );

  const placeholder = useMemo(() => {
    if (!selectedProduct) {
      return formatMessage({ id: 'forecast.assistant.cycleSelect' });
    }

    const payload = get(selectedProduct, 'product.payload');
    const compartmentName = getCompartmentName(compartments, payload?.compartmentRef?.id);
    const groupName = getGroupName(payload, varieties, fruitClasses, intl);

    return `${[compartmentName, groupName].filter(Boolean).join(' • ')}, ${getPlantingCycleDates(payload, intl)}`;
  }, [selectedProduct, compartments, varieties, fruitClasses, intl, formatMessage]);

  const handleSelect = useCallback((productId) => {
    onSelect(productId);
    setOpen(false);
  }, [onSelect]);

  const renderRow = (item) => {
    const id = get(item, 'product.id');
    const payload = get(item, 'product.payload');
    const isSelected = Number(selectedId) === Number(id);

    return (
      <div
        key={`forecastCycleOption-${id}`}
        className={classnames(
          styles.option,
          selectStyles.option,
          { [selectStyles.selected]: isSelected }
        )}
        onClick={() => handleSelect(id)}
        role='button'
        tabIndex={0}
      >
        <div className={styles.cycleDates}>{getPlantingCycleDates(payload, intl)}</div>
        <div className={styles.cycleCompartment}>{getCompartmentName(compartments, payload?.compartmentRef?.id)}</div>
      </div>
    );
  };

  const renderGroups = () => map(groupedByVariety, (items, varietyId) => {
    const sorted = sortBy(items, item => get(item, 'product.payload.startDate')).reverse();
    const groupName = getGroupName(get(sorted, '[0].product.payload'), varieties, fruitClasses, intl);

    return (
      <div key={`forecastCycleGroup-${varietyId}`}>
        <div className={styles.cycleVarietyName}>{groupName}</div>
        <div>{sorted.map(renderRow)}</div>
      </div>
    );
  });

  return (
    <Select
      classNameButton={styles.longSelectButton}
      classNamePopup={styles.longSelectPopup}
      placeholder={placeholder}
      theme='white'
      size='medium'
      open={open}
      onOpenChange={setOpen}
      disabled={cycleProducts.length === 0}
      textEllipsis
    >
      <div className={selectStyles.options}>
        <div className={styles.optionsHeader}>
          <span>{formatMessage({ id: 'plantingCycles.plantingCycles' })}</span>
        </div>
        {renderGroups()}
      </div>
    </Select>
  );
};

HarvestForecastCycleSelect.propTypes = {
  intl: intlShape.isRequired,
  productsList: PropTypes.array,
  varieties: PropTypes.array.isRequired,
  fruitClasses: PropTypes.array.isRequired,
  compartments: PropTypes.array.isRequired,
  selectedId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  onSelect: PropTypes.func.isRequired,
};

HarvestForecastCycleSelect.defaultProps = {
  productsList: [],
  selectedId: null,
};

export default HarvestForecastCycleSelect;
